/**
 * Whether a screen is currently offering its rows for selection.
 *
 * Three screens carry bulk actions, and all three used to draw a checkbox on every
 * row forever — forty boxes for a thing done occasionally, and on the session list
 * that column was the *only* way into a selection. The boxes are now behind a
 * toggle, and the toggle is what is permanent instead: one small control that says
 * the rows can be selected, rather than forty that take space to say it.
 *
 * The state is **derived**, and that is the whole design:
 *
 *     selecting = manuallyOn || selected.length > 0
 *
 * A selection that exists while the boxes are hidden is a selection the operator
 * cannot see, and the account pool already had the sentence for it in its own
 * source — "a selection an operator cannot see is worse than none". Deriving the
 * mode from the selection makes that state unrepresentable: a selection cannot
 * outlive the boxes that show it. It also means nothing extra has to be persisted.
 * The session list stores its selection (`kimene.lo.selected`), so a reload comes
 * back with the mode already on, without a second key that could disagree with it.
 *
 * Turning the toggle off clears the selection, for the same reason — leaving it
 * would recreate exactly the state the derivation exists to forbid.
 */

import { useCallback, useState } from 'react'

export interface SelectionMode {
  /** Draw the checkboxes, and the bulk bar. */
  selecting: boolean
  /** For the toggle's `onClick`. */
  toggle: () => void
}

/**
 * @param selected the ids currently selected, so the mode can be derived from them.
 * @param clear called when the operator turns selection off.
 */
export function useSelectionMode(selected: readonly string[], clear: () => void): SelectionMode {
  const [manuallyOn, setManuallyOn] = useState(false)

  const selecting = manuallyOn || selected.length > 0

  const toggle = useCallback(() => {
    if (selecting) {
      setManuallyOn(false)
      clear()
      return
    }
    setManuallyOn(true)
  }, [selecting, clear])

  return { selecting, toggle }
}
